"use client";

import {
  ChevronRight,
  ClipboardPaste,
  NotebookPen,
  PenLine,
  Upload,
  Youtube,
} from "lucide-react";
import { useRouter } from "next/navigation";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { AnkiIcon } from "@/components/ui/icons";
import { useCreateNotes } from "@/features/ai-notetaker/api/use-create-notes";
import useAuthModal from "@/features/auth/hooks/use-auth-modal";
import useImportFileModal from "@/features/files/hooks/use-import-modal";
import { useCreateStudyset } from "@/features/studysets/api/use-create-studyset";
import { cn } from "@/lib/utils";
import { useI18n } from "@/locales/client";

const uploadMethods = [
  {
    icon: Upload,
    title: "importFromFile",
    description: "importFromFileDescription",
    value: "file_upload",
    comingSoon: false,
  },
  {
    icon: ClipboardPaste,
    title: "pasteText",
    description: "pasteTextDescription",
    value: "paste_text",
    comingSoon: false,
  },
  {
    icon: Youtube,
    title: "youtubeLink",
    description: "youtubeLinkDescription",
    value: "youtube_url",
    comingSoon: false,
  },
  {
    icon: PenLine,
    title: "createManually",
    description: "createManuallyDescription",
    value: "create_manually",
    comingSoon: false,
  },
  {
    icon: NotebookPen,
    title: "aiNotetaker",
    description: "aiNotetakerDescription",
    value: "ai_notetaker",
    comingSoon: false,
  },
  {
    icon: AnkiIcon,
    title: "importFromAnki",
    description: "importFromAnkiDescription",
    value: "anki_import",
    comingSoon: false,
  },
] as const;

interface UploadMethodsProps {
  isAuthenticated: boolean;
}

export const UploadMethods = ({ isAuthenticated }: UploadMethodsProps) => {
  const { onOpen: openFileUpload } = useImportFileModal();
  const { onOpen: onOpenAuth } = useAuthModal();
  const router = useRouter();
  const t = useI18n();
  const _createStudySetMutation = useCreateStudyset();
  const createNotesMutation = useCreateNotes();
  return (
    <>
      {uploadMethods.map((method) => (
        <Card
          onClick={() => {
            if (method.comingSoon) return;
            if (!isAuthenticated) {
              onOpenAuth();
            } else if (method.value === "create_manually") {
              router.push("/studysets/new");
            } else if (method.value === "ai_notetaker") {
              createNotesMutation.mutate({
                name: "Untitled Note",
              });
            } else {
              openFileUpload(method.value);
            }
          }}
          key={method.value}
          className={cn(
            "bg-muted/40 transition-colors relative group",
            method.comingSoon
              ? "cursor-default opacity-60"
              : "cursor-pointer hover:bg-muted/80",
          )}
        >
          <CardContent className="p-5 flex items-start w-full gap-4">
            <method.icon className="size-6 text-brand-primary flex-shrink-0" />
            <div className="flex flex-col gap-1.5 flex-1">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-base text-foreground">
                  {t(method.title)}
                </h3>
                {method.comingSoon && (
                  <Badge
                    variant="secondary"
                    className="text-[10px] px-1.5 py-0"
                  >
                    {t("comingSoon")}
                  </Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground leading-5">
                {t(method.description)}
              </p>
            </div>
          </CardContent>
          {!method.comingSoon && (
            <ChevronRight className="size-6 text-muted-foreground absolute right-4 top-5 stroke-1 transition-transform duration-200 group-hover:translate-x-1" />
          )}
        </Card>
      ))}
    </>
  );
};
